import { Card, BlockStack, InlineStack, Text, Badge, Divider } from '@shopify/polaris';

export interface BreakevenResult {
  price: number;
  cogs: number;
  gateway_fee: number;
  shipping: number;
  variable_cost: number;
  contribution_margin: number;
  contribution_margin_pct: number | null;
  breakeven_roas: number | null;
  target_roas: Record<string, number | null>;
  profitable: boolean;
}

export interface BreakevenVariant {
  variant_id: number;
  title: string | null;
  price: number;
  cost: number;
  breakeven: BreakevenResult;
}

const roas = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(2)}×`);
const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`);

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <BlockStack gap="050">
      <Text as="span" variant="bodySm" tone="subdued">
        {label}
      </Text>
      <Text as="span" variant="bodyMd" fontWeight="semibold">
        {value}
      </Text>
    </BlockStack>
  );
}

/**
 * Breakeven ROAS per variant: the ad return needed to cover a unit's own costs,
 * plus the higher ROAS for a 20% / 30% net margin. Fed by GET /products/{id}.
 */
export default function BreakevenRoasWidget({
  variants,
  formatMoney,
}: {
  variants: BreakevenVariant[] | undefined;
  formatMoney: (v: number | string) => string;
}) {
  if (!variants || variants.length === 0) {
    return (
      <Text as="p" variant="bodySm" tone="subdued">
        Add a price in Shopify (and a product cost below) to see the breakeven ROAS.
      </Text>
    );
  }

  return (
    <BlockStack gap="300">
      {variants.map((v) => {
        const b = v.breakeven;
        return (
          <Card key={v.variant_id} background="bg-surface-secondary">
            <BlockStack gap="200">
              {variants.length > 1 && (
                <Text as="span" variant="bodySm" fontWeight="semibold">
                  {v.title ?? `Variant ${v.variant_id}`}
                </Text>
              )}
              <InlineStack gap="400" wrap>
                <Metric label="Price" value={formatMoney(b.price)} />
                <Metric label="COGS" value={formatMoney(b.cogs)} />
                <Metric label="Gateway fee" value={formatMoney(b.gateway_fee)} />
                <Metric
                  label="Contribution / unit"
                  value={`${formatMoney(b.contribution_margin)} (${pct(b.contribution_margin_pct)})`}
                />
              </InlineStack>

              <Divider />

              {b.profitable ? (
                <InlineStack gap="500" wrap blockAlign="center">
                  <InlineStack gap="150" blockAlign="center">
                    <Text as="span" variant="bodySm" tone="subdued">
                      🎯 Breakeven ROAS
                    </Text>
                    <Badge tone="attention">{roas(b.breakeven_roas)}</Badge>
                  </InlineStack>
                  <Metric label="Target 20% margin" value={roas(b.target_roas['20'])} />
                  <Metric label="Target 30% margin" value={roas(b.target_roas['30'])} />
                </InlineStack>
              ) : (
                <Text as="p" variant="bodySm" tone="critical">
                  Loses {formatMoney(Math.abs(b.contribution_margin))} per unit before any ad spend — no
                  ROAS can make it profitable. Raise the price or lower the cost.
                </Text>
              )}
            </BlockStack>
          </Card>
        );
      })}
    </BlockStack>
  );
}
